/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useState } from 'react';
import { Box, Flex, HStack, VStack, Collapsible } from 'chakra-v3';
import { Text } from '../../components/Text';
import { MdKeyboardArrowDown } from 'react-icons/md';

interface ReadMoreBlockProps {
  title: string;
  introText: string;
  expandedContent: {
    leftColumn: string;
    rightColumn: string;
  };
  defaultExpanded?: boolean;
}

const ReadMoreBlock: React.FC<ReadMoreBlockProps> = ({
  title,
  introText,
  expandedContent,
  defaultExpanded = false,
}) => {
  const [isOpen, setIsOpen] = useState(defaultExpanded);

  return (
    <Box bg="transparent" w="100%">
      <Collapsible.Root open={isOpen} onOpenChange={(e) => setIsOpen(e.open)}>
        <VStack
          gap={{ base: '12px', sm: '8px' }}
          alignItems="stretch"
          pt={{ base: '20px', sm: '24.8px' }}
          px={{ base: '16px', sm: '24.8px' }}
          pb={{ base: '10px', sm: '0.8px' }}
        >
          {/* Title and Toggle Button Row */}
          <Flex
            alignItems="flex-start"
            justifyContent="space-between"
            w="100%"
            gap={{ base: '12px', md: '0px' }}
            direction={{ base: 'column', md: 'row' }}
          >
            <Text
              fontFamily="heading"
              fontSize={{ base: '18px', sm: '22px', md: '28px', lg: '32px' }}
              fontWeight="normal"
              lineHeight={{ base: '1.25', md: '40px', lg: '48px' }}
              color="white"
              flex="1"
            >
              {title}
            </Text>
            <Collapsible.Trigger asChild>
              <Flex
                as="button"
                gap="8px"
                alignItems="center"
                bg="transparent"
                border="none"
                p={0}
                cursor="pointer"
                _hover={{ opacity: 0.8 }}
                transition="opacity 0.2s"
                h="21px"
                flexShrink={0}
                alignSelf={{ base: 'flex-end', md: 'center' }}
                display={{ base: 'none', md: 'flex' }}
              >
                <Text
                  fontFamily="body"
                  fontSize="14px"
                  fontWeight="500"
                  lineHeight="21px"
                  color="#addeeb"
                  whiteSpace="nowrap"
                >
                  {isOpen ? 'Read less' : 'Read more'}
                </Text>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                  transition="transform 0.3s ease"
                  w="20px"
                  h="20px"
                  flexShrink={0}
                >
                  <MdKeyboardArrowDown size={20} color="#addeeb" />
                </Box>
              </Flex>
            </Collapsible.Trigger>
          </Flex>

          {/* Content */}
          <VStack gap="8px" alignItems="stretch" w="100%">
            {/* Introductory Paragraph */}
            <Text
              fontFamily="body"
              fontSize={{ base: '15px', sm: '16px', md: '16px', lg: '16px' }}
              fontWeight="400"
              lineHeight={{ base: '21.6px', sm: '22.4px' }}
              color="white"
              w="100%"
              whiteSpace="pre-wrap"
            >
              {introText}
            </Text>

            {/* Mobile Read More Trigger (below intro text) */}
            <Collapsible.Trigger asChild>
              <Flex
                as="button"
                gap="8px"
                alignItems="center"
                bg="transparent"
                border="none"
                p={0}
                cursor="pointer"
                _hover={{ opacity: 0.8 }}
                transition="opacity 0.2s"
                h="21px"
                flexShrink={0}
                alignSelf="flex-start"
                display={{ base: 'flex', md: 'none' }}
                mt={{ base: '8px', md: '0' }}
              >
                <Text
                  fontFamily="body"
                  fontSize="14px"
                  fontWeight="500"
                  lineHeight="21px"
                  color="#addeeb"
                  whiteSpace="nowrap"
                >
                  {isOpen ? 'Read less' : 'Read more'}
                </Text>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                  transition="transform 0.3s ease"
                  w="20px"
                  h="20px"
                  flexShrink={0}
                >
                  <MdKeyboardArrowDown size={20} color="#addeeb" />
                </Box>
              </Flex>
            </Collapsible.Trigger>

            {/* Expanded Content - Two Columns */}
            <Collapsible.Content>
              <HStack
                gap={{ base: '12px', md: 4 }}
                alignItems="flex-start"
                flexDirection={{ base: 'column', md: 'row' }}
                w="100%"
              >
                <Text
                  fontFamily="body"
                  fontSize={{ base: '15px', sm: '16px', md: '16px', lg: '16px' }}
                  fontWeight="400"
                  lineHeight={{ base: '21.6px', sm: '22.4px' }}
                  color="white"
                  flex="1 0 0"
                  whiteSpace="pre-wrap"
                >
                  {expandedContent.leftColumn}
                </Text>
                <Text
                  fontFamily="body"
                  fontSize={{ base: '15px', sm: '16px', md: '16px', lg: '16px' }}
                  fontWeight="400"
                  lineHeight={{ base: '21.6px', sm: '22.4px' }}
                  color="white"
                  flex="1 0 0"
                  whiteSpace="pre-wrap"
                >
                  {expandedContent.rightColumn}
                </Text>
              </HStack>
            </Collapsible.Content>
          </VStack>
        </VStack>
      </Collapsible.Root>
    </Box>
  );
};

export default ReadMoreBlock;
